import { memo, useCallback, useEffect, useRef, useState } from 'react'

import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'

import styles from './ContactRolesRow.module.scss'

type ContactRolesRowProps = {
  roles?: string[]
}

const ELLIPSIS_WIDTH = 36

export const ContactRolesRow = memo<ContactRolesRowProps>(({ roles = [] }) => {
  const rowRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [visibleRoles, setVisibleRoles] = useState<string[]>(roles)
  const [hiddenRoles, setHiddenRoles] = useState<string[]>([])

  const recalc = useCallback(() => {
    const row = rowRef.current
    const measureEl = measureRef.current
    if (!row || !measureEl) return

    const availableWidth = row.clientWidth
    if (!availableWidth) return

    const children = Array.from(measureEl.children) as HTMLElement[]
    const computedStyles = getComputedStyle(measureEl)
    const gap =
      parseFloat(computedStyles.columnGap || computedStyles.gap || '0') ||
      parseFloat(computedStyles.rowGap || '0') ||
      0

    const totalWidth =
      children.reduce((sum, child) => sum + child.offsetWidth, 0) +
      gap * Math.max(0, children.length - 1)

    // All fit, nothing to hide
    if (totalWidth <= availableWidth) {
      setVisibleRoles(roles)
      setHiddenRoles([])
      return
    }

    const budget = availableWidth - ELLIPSIS_WIDTH
    let used = 0
    let fit = 0

    for (let i = 0; i < children.length; i += 1) {
      const childWidth = children[i].offsetWidth
      const nextUsed = used + childWidth + (i > 0 ? gap : 0)
      if (nextUsed > budget) {
        break
      }
      used = nextUsed
      fit = i + 1
    }

    // Ensure at least one visible
    if (fit === 0) {
      fit = 1
    }

    const nextVisible = roles.slice(0, fit)
    const nextHidden = roles.slice(fit)

    // Avoid pointless state updates to prevent flicker
    setVisibleRoles((prev) =>
      prev.length === nextVisible.length && prev.every((r, idx) => r === nextVisible[idx])
        ? prev
        : nextVisible
    )
    setHiddenRoles((prev) =>
      prev.length === nextHidden.length && prev.every((r, idx) => r === nextHidden[idx])
        ? prev
        : nextHidden
    )
  }, [roles])

  useEffect(() => {
    recalc()
  }, [roles, recalc])

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const observer = new ResizeObserver(() => recalc())
    observer.observe(row)
    return () => observer.disconnect()
  }, [recalc])

  if (!roles.length) {
    return null
  }

  return (
    <div className={styles.contactRolesRow} ref={rowRef}>
      <div className={styles.contactRoles}>
        {visibleRoles.map((role) => (
          <span key={role} className={styles.contactRole}>
            {role}
          </span>
        ))}
      </div>
      {hiddenRoles.length ? (
        <AbsolutePopup
          rootClassName={styles.contactRolesOverflow}
          popupClassName={styles.contactRolesPopupWrapper}
          verticalStartPosition={TPopupVertical.Bottom}
          verticalDirection={TPopupVertical.Bottom}
          horizontalStartPosition={TPopupHorizontal.Right}
          horizontalDirection={TPopupHorizontal.Right}
          verticalOffset={4}
          visibilityTrigger={TVisibilityTrigger.Hover}
          popupContent={() => (
            <PopPaper className={styles.contactRolesPopover} shadowPosition="bottom">
              {hiddenRoles.map((role) => (
                <span key={`popover-${role}`} className={styles.contactRole}>
                  {role}
                </span>
              ))}
            </PopPaper>
          )}
        >
          <span className={styles.contactRoleEllipsis}>...</span>
        </AbsolutePopup>
      ) : null}

      {/* Hidden measurer to keep layout stable */}
      <div className={styles.contactRolesMeasure} ref={measureRef} aria-hidden>
        {roles.map((role) => (
          <span key={`measure-${role}`} className={styles.contactRole}>
            {role}
          </span>
        ))}
      </div>
    </div>
  )
})

ContactRolesRow.displayName = 'ContactRolesRow'
